// Layout.jsx — Updated menu with new screens and correct admin access
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const MENU = {
  hospital_staff: [
    { path: "/upload",   icon: "⬆", label: "Upload billing file" },
    { path: "/expenses", icon: "📋", label: "Enter expenses" },
    { path: "/tally",    icon: "💰", label: "Cash tally" },
    { path: "/status",   icon: "✓",  label: "Upload status" },
  ],
  accountant_staff: [
    { path: "/dashboard", icon: "◉", label: "Dashboard" },
    { path: "/entries",   icon: "≡", label: "Journal entries" },
    { path: "/export",    icon: "⬇", label: "Export" },
    { path: "/tpa",       icon: "🏥", label: "TPA aging" },
    { path: "/assets",    icon: "🏛", label: "Fixed assets" },
  ],
  iicipl_admin: [
    // Entities and overview
    { path: "/entities",    icon: "🏥", label: "Entities" },
    { path: "/admin",       icon: "◉",  label: "Admin overview" },
    // Daily operations — admin sees these too
    { path: "/upload",      icon: "⬆",  label: "Billing upload" },
    { path: "/expenses",    icon: "📋", label: "Enter expenses" },
    { path: "/dashboard",   icon: "📊", label: "Dashboard" },
    // Accounting
    { path: "/entries",     icon: "≡",  label: "Journal entries" },
    { path: "/manual-entry",icon: "✏",  label: "Manual entry" },
    { path: "/salary",      icon: "👥", label: "Salary journal" },
    { path: "/statutory",   icon: "🏛", label: "Statutory payments" },
    { path: "/export",      icon: "⬇",  label: "Export" },
    // Debtors
    { path: "/tpa",         icon: "🏥", label: "TPA aging" },
    { path: "/tpa-settle",  icon: "✓",  label: "TPA settlement" },
    // Setup
    { path: "/mapping",     icon: "⚙",  label: "Column mapping" },
    { path: "/zoho",        icon: "Z",  label: "Zoho setup" },
    { path: "/ledgers",     icon: "📒", label: "Create ledger" },
    { path: "/assets",      icon: "🏛", label: "Fixed assets" },
    { path: "/users",       icon: "👤", label: "Users" },
  ],
};

export default function Layout({ children, user, entity, onLogout }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const menu = MENU[user?.role] || [];
  const activeItem = menu.find(item => location.pathname === item.path || location.pathname.startsWith(item.path + "/"));
  const title = activeItem?.label || "TaxStuff360 Healthcare";

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f0f4f8", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <aside style={{ width: collapsed ? 60 : 220, background: "#1B3F6E", display: "flex", flexDirection: "column", transition: "width .2s", flexShrink: 0, overflowY: "auto" }}>

        {/* Logo */}
        <div style={{ padding: collapsed ? "20px 0" : "20px 16px", borderBottom: "1px solid #2d5a9e", flexShrink: 0 }}>
          {!collapsed && (<><div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>TaxStuff360</div><div style={{ color: "#7FB3D3", fontSize: 11, marginTop: 2 }}>Healthcare</div></>)}
          {collapsed && <div style={{ color: "#fff", textAlign: "center", fontSize: 18, fontWeight: 700 }}>T</div>}
        </div>

        {/* Entity badge */}
        {!collapsed && entity && (
          <div style={{ padding: "10px 16px", background: "#163459", borderBottom: "1px solid #2d5a9e", flexShrink: 0 }}>
            <div style={{ color: "#7FB3D3", fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em" }}>Entity</div>
            <div style={{ color: "#fff", fontSize: 12, fontWeight: 500, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{entity.name}</div>
            {entity.location && <div style={{ color: "#7FB3D3", fontSize: 10, marginTop: 1 }}>{entity.location}</div>}
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: "8px 0" }}>
          {menu.map(item => {
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <button key={item.path} onClick={() => navigate(item.path)} style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: collapsed ? "9px 0" : "9px 16px",
                background: active ? "#2E86AB" : "transparent", border: "none",
                color: active ? "#fff" : "#AED6F1", fontSize: 12, cursor: "pointer",
                textAlign: "left", justifyContent: collapsed ? "center" : "flex-start",
                borderLeft: active ? "3px solid #7FC8E8" : "3px solid transparent",
                transition: "background .15s",
              }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* User + logout */}
        <div style={{ padding: collapsed ? "12px 0" : "12px 16px", borderTop: "1px solid #2d5a9e", flexShrink: 0 }}>
          {!collapsed && <div style={{ color: "#7FB3D3", fontSize: 11, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>}
          <button onClick={onLogout} style={{ width: "100%", padding: collapsed ? "6px 0" : "6px 10px", background: "transparent", border: "1px solid #2d5a9e", color: "#AED6F1", borderRadius: 6, fontSize: 12, cursor: "pointer", textAlign: collapsed ? "center" : "left" }}>
            {collapsed ? "×" : "Sign out"}
          </button>
        </div>

        <button onClick={() => setCollapsed(c => !c)} style={{ background: "#163459", border: "none", color: "#7FB3D3", padding: "8px", fontSize: 14, cursor: "pointer", borderTop: "1px solid #2d5a9e", flexShrink: 0 }}>
          {collapsed ? "→" : "←"}
        </button>
      </aside>

      <main style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        <header style={{ minHeight: 58, background: "#fff", borderBottom: "1px solid #E0E7EF", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "0 20px", flexShrink: 0, position: "sticky", top: 0, zIndex: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1B3F6E" }}>{title}</div>
            {entity && <div style={{ fontSize: 12, color: "#607D8B", marginTop: 2 }}>{entity.name}{entity.location ? ` · ${entity.location}` : ""}</div>}
          </div>
          <div style={{ fontSize: 12, color: "#607D8B", whiteSpace: "nowrap" }}>{user?.name}</div>
        </header>
        <div style={{ flex: 1, padding: 20 }}>{children}</div>
      </main>
    </div>
  );
}
